import { apiClient } from "@/lib/apiClient";
import i18n from "@/utils/i18n";
import type {
  BlackListEntry,
  BlackListFilters,
  BannedUserApiItem,
  BannedUserListResponseApi,
  CreateBannedUserDto,
  UpdateBannedUserDto,
} from "../types/black-list.types";
import { apiReasonToUi, uiReasonToApi } from "../types/black-list.types";

const ENDPOINT = "/api/admin/banned-users";

/** Backend date (ISO or YYYY-MM-DD) -> display format DD.MM.YYYY */
function formatDisplayDate(isoOrDate: string | undefined): string {
  if (!isoOrDate) return "—";
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return isoOrDate;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

/** Form date (DD.MM.YYYY) -> API ISO 8601 (e.g. 2025-01-01T00:00:00.000Z) */
function formDateToIso8601(value: string | undefined): string | undefined {
  if (!value?.trim()) return undefined;
  const parts = value.trim().split(".");
  if (parts.length !== 3) return value;
  const [d, m, y] = parts.map(Number);
  if (Number.isNaN(d) || Number.isNaN(m) || Number.isNaN(y)) return value;
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString();
}

/** Aynı gün seçildiğinde endDate'i gün sonuna ayarla (tek günlük yasak için start < end) */
function normalizeEndDateForSameDay(
  startDate: string | undefined,
  endDate: string | undefined,
): string | undefined {
  if (!startDate?.trim() || !endDate?.trim()) return formDateToIso8601(endDate);
  const startParts = startDate.trim().split(".");
  const endParts = endDate.trim().split(".");
  if (startParts.length !== 3 || endParts.length !== 3) return formDateToIso8601(endDate);
  const [sd, sm, sy] = startParts.map(Number);
  const [ed, em, ey] = endParts.map(Number);
  if (sd === ed && sm === em && sy === ey) {
    const date = new Date(ey, em - 1, ed, 23, 59, 59, 999);
    return date.toISOString();
  }
  return formDateToIso8601(endDate);
}

function fullName(ref: { firstName?: string; lastName?: string } | undefined): string {
  if (!ref) return "—";
  const parts = [ref.firstName, ref.lastName].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
}

function mapApiItemToEntry(item: BannedUserApiItem): BlackListEntry {
  const bannedPersonName = fullName(item.user) !== "—" ? fullName(item.user) : (item.personnelName ?? "—");
  const personnelId = item.user?.id ?? item.userId ?? item.personnelId ?? "";
  return {
    id: item.id,
    personnelId,
    personnelName: bannedPersonName,
    bannedPersonName,
    bannedByPersonName: fullName(item.bannedByUser),
    startDate: formatDisplayDate(item.startDate),
    endDate: formatDisplayDate(item.endDate),
    reason: apiReasonToUi(item.reason),
    tcKimlikNo: item.user?.identityNumber ?? item.identityNumber ?? item.id,
    description: item.description,
    supportingDocuments: item.supportingDocuments,
    files: item.files,
  };
}

export interface BlackListGetAllParams {
  page?: number;
  pageSize?: number;
  sortBy?: "startDate" | "endDate" | "bannedAt" | "createdAt";
  sortOrder?: "asc" | "desc";
  filters?: BlackListFilters;
}

export const blackListApi = {
  /**
   * GET /api/admin/banned-users - List banned users with pagination and filtering.
   */
  getAll: async (params?: BlackListGetAllParams): Promise<{ data: BlackListEntry[]; total: number }> => {
    const query: Record<string, unknown> = {
      page: params?.page ?? 1,
      limit: params?.pageSize ?? 10,
    };
    if (params?.filters?.search) query.search = params.filters.search;
    const reason = params?.filters?.reason;
    const validReasons = [i18n.t("blackList.absence"), i18n.t("blackList.failure")] as const;
    const isValidReason = (r: unknown): r is (typeof validReasons)[number] =>
      typeof r === "string" && validReasons.includes(r as (typeof validReasons)[number]);
    if (reason !== undefined && reason !== null) {
      const arr = Array.isArray(reason) ? reason : [reason];
      const filtered = arr.filter(isValidReason);
      if (filtered.length === 1) {
        query.reason = uiReasonToApi(filtered[0]);
      } else if (filtered.length > 1) {
        query.reason = filtered.map(uiReasonToApi);
      }
    }
    if (params?.sortBy) query.sortBy = params.sortBy;
    if (params?.sortOrder) query.sortOrder = params.sortOrder;

    const { data } = await apiClient.get<BannedUserListResponseApi>(ENDPOINT, {
      params: query,
    });
    const list = data?.data ?? [];
    const total = data?.count ?? data?.total ?? list.length;
    return {
      data: list.map(mapApiItemToEntry),
      total,
    };
  },

  /**
   * GET /api/admin/banned-users/{id}
   */
  getById: async (id: string): Promise<BlackListEntry> => {
    const { data } = await apiClient.get<{ data?: BannedUserApiItem }>(
      `${ENDPOINT}/${id}`,
    );
    if (!data?.data) throw new Error(i18n.t("blackList.notFound"));
    return mapApiItemToEntry(data.data);
  },

  /**
   * DELETE /api/admin/banned-users/{id} - Soft delete.
   */
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`${ENDPOINT}/${id}`);
  },

  /**
   * POST /api/admin/banned-users - CreateBannedUserDto: userId, reason, startDate, endDate, description?, fileIds?
   */
  create: async (body: {
    userId: string;
    reason: BlackListEntry["reason"];
    startDate: string;
    endDate: string;
    description?: string;
    fileIds?: string[];
  }): Promise<BlackListEntry> => {
    const payload: CreateBannedUserDto = {
      userId: body.userId,
      reason: uiReasonToApi(body.reason),
      startDate: formDateToIso8601(body.startDate) ?? body.startDate,
      endDate: normalizeEndDateForSameDay(body.startDate, body.endDate) ?? body.endDate,
      description: body.description,
      fileIds: body.fileIds?.length ? body.fileIds : undefined,
    };
    const { data } = await apiClient.post<{ data?: BannedUserApiItem }>(
      ENDPOINT,
      payload,
    );
    if (!data?.data) throw new Error(i18n.t("blackList.createFailed"));
    return mapApiItemToEntry(data.data);
  },

  /**
   * PATCH /api/admin/banned-users/{id} - reason, startDate, endDate, description, fileIds (Swagger)
   */
  update: async (
    id: string,
    body: {
      reason?: BlackListEntry["reason"];
      startDate?: string;
      endDate?: string;
      description?: string;
      fileIds?: string[];
    },
  ): Promise<BlackListEntry> => {
    const payload: UpdateBannedUserDto = {};
    if (body.reason !== undefined) payload.reason = uiReasonToApi(body.reason);
    if (body.startDate !== undefined) payload.startDate = formDateToIso8601(body.startDate) ?? body.startDate;
    if (body.endDate !== undefined) payload.endDate = normalizeEndDateForSameDay(body.startDate, body.endDate) ?? body.endDate;
    if (body.description !== undefined) payload.description = body.description;
    if (body.fileIds !== undefined && body.fileIds.length > 0) payload.fileIds = body.fileIds;

    const { data } = await apiClient.patch<{ data?: BannedUserApiItem }>(
      `${ENDPOINT}/${id}`,
      payload,
    );
    if (!data?.data) throw new Error(i18n.t("blackList.updateFailed"));
    return mapApiItemToEntry(data.data);
  },
};
